import React, { useCallback, useRef, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon, FilterIcon, TextSearchIcon, XIcon } from 'lucide-react';
import { sortLabels } from '../../utils/columns';
import { Popover } from '../ui/Popover';
import { SearchField } from '../ui/SearchField';
import { ValueChecklist } from '../ui/ValueChecklist';
import { iconFor } from './columnIcons';

export function ColumnHeaderMenu({ columnKey, meta, sort, onSort, options, selected, onFilterChange, textFilter, onTextFilterChange }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const close = useCallback(() => setOpen(false), []);
  const labels = sortLabels(meta.sortType);
  const direction = sort?.key === columnKey ? sort.direction : null;
  const filtered = selected.length > 0 || textFilter.length > 0;

  const applySort = dir => {
    onSort(direction === dir ? null : { key: columnKey, direction: dir });
  };

  const applyContains = () => {
    if (!query.trim()) return;
    onTextFilterChange(query.trim());
    setQuery('');
  };

  const ColumnIcon = iconFor(columnKey);
  const SortIcon = direction === 'asc' ? ArrowUpIcon : direction === 'desc' ? ArrowDownIcon : ChevronsUpDownIcon;
  const visibleValues = query.trim()
    ? options.filter(o => o.value.toLowerCase().includes(query.trim().toLowerCase())).map(o => o.value)
    : options.map(o => o.value);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={meta.label}
        className={`tw-group/head -tw-mx-2 tw-flex tw-w-[calc(100%+1rem)] tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2 tw-py-1 tw-text-left tw-text-xs tw-font-medium tw-transition-colors tw-duration-100 hover:tw-bg-neutral-200/60 hover:tw-text-neutral-900 ${direction || filtered ? 'tw-text-neutral-900' : 'tw-text-neutral-500'}`}>
        <ColumnIcon className={`tw-h-3.5 tw-w-3.5 tw-shrink-0 ${direction || filtered ? 'tw-text-brand-600' : 'tw-text-neutral-400'}`} aria-hidden />
        <span className="tw-truncate">{meta.label}</span>
        {filtered && <FilterIcon className="tw-h-3 tw-w-3 tw-shrink-0 tw-fill-brand-600 tw-text-brand-600" aria-label="Filtered" />}
        <SortIcon className={`tw-h-3.5 tw-w-3.5 tw-shrink-0 ${direction ? 'tw-text-brand-600' : 'tw-text-neutral-400 tw-opacity-60 group-hover/head:tw-opacity-100'}`} aria-hidden />
      </button>
      <Popover anchorRef={anchorRef} open={open} onClose={close} label={`${meta.label} options`} width={272}>
        <div className="tw-p-1.5">
          {['asc', 'desc'].map(dir => {
            const Icon = dir === 'asc' ? ArrowUpIcon : ArrowDownIcon;
            const active = direction === dir;
            return (
              <button
                key={dir}
                type="button"
                onClick={() => applySort(dir)}
                aria-pressed={active}
                className={`tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-left tw-text-sm tw-transition-colors tw-duration-100 ${active ? 'tw-bg-brand-50 tw-text-brand-600' : 'tw-text-neutral-900 hover:tw-bg-sand-50'}`}>
                <Icon className="tw-h-4 tw-w-4" aria-hidden />
                <span className="tw-flex-1">{labels[dir]}</span>
                {active && <span className="tw-text-xs">Clear</span>}
              </button>
            );
          })}
        </div>
        <div className="tw-border-t tw-border-neutral-200 tw-px-3 tw-pb-2 tw-pt-3">
          <p className="tw-mb-2 tw-text-xs tw-font-medium tw-text-neutral-500">Filter by value</p>
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Type to search or match…"
            label={`Search ${meta.label} values`}
            autoFocus
            onKeyDown={e => e.key === 'Enter' && applyContains()}
          />
          {query.trim() && (
            <button
              type="button"
              onClick={applyContains}
              className="tw-mt-2 tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-md tw-bg-brand-50 tw-px-2.5 tw-py-1.5 tw-text-left tw-text-sm tw-text-brand-600 tw-transition-colors hover:tw-bg-brand-100">
              <TextSearchIcon className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden />
              <span className="tw-truncate">Show rows containing “{query.trim()}”</span>
            </button>
          )}
          {textFilter && (
            <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-rounded-md tw-bg-sand-50 tw-px-2.5 tw-py-1.5 tw-text-sm">
              <span className="tw-truncate tw-text-neutral-900">Contains “{textFilter}”</span>
              <button type="button" onClick={() => onTextFilterChange('')} aria-label="Remove text filter" className="tw-rounded tw-p-0.5 tw-text-neutral-400 hover:tw-text-neutral-900">
                <XIcon className="tw-h-3.5 tw-w-3.5" />
              </button>
            </div>
          )}
        </div>
        <ValueChecklist options={options} selected={selected} onChange={onFilterChange} query={query} />
        <div className="tw-flex tw-items-center tw-justify-between tw-border-t tw-border-neutral-200 tw-px-3 tw-py-2">
          <button
            type="button"
            onClick={() => onFilterChange(Array.from(new Set([...selected, ...visibleValues])))}
            className="tw-text-sm tw-font-medium tw-text-brand-600 hover:tw-underline">
            Select all
          </button>
          <button
            type="button"
            disabled={!filtered}
            onClick={() => {
              onFilterChange([]);
              onTextFilterChange('');
            }}
            className="tw-text-sm tw-font-medium tw-text-neutral-500 hover:tw-text-neutral-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-40">
            Clear filter
          </button>
        </div>
      </Popover>
    </>
  );
}
